import { useEffect, useState } from "react";
import { X, Hash, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

export function TagsInput({ tags = [], setTags, testid = "tags-input" }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    api.get("/tags").then((r) => setSuggestions(r.data || [])).catch(() => {});
  }, []);

  const add = (raw) => {
    const name = String(raw || "").trim().replace(/^#+/, "").trim();
    if (!name) return;
    if (tags.some((t) => t.toLowerCase() === name.toLowerCase())) {
      setInput("");
      return;
    }
    setTags([...tags, name]);
    setInput("");
    if (!suggestions.some((s) => s.toLowerCase() === name.toLowerCase())) {
      setSuggestions((prev) => [...prev, name].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" })));
    }
  };

  const remove = (i) => setTags(tags.filter((_, idx) => idx !== i));

  const deleteSuggestion = async (name) => {
    try {
      await api.delete(`/tags?name=${encodeURIComponent(name)}`);
      setSuggestions((prev) => prev.filter((s) => s.toLowerCase() !== name.toLowerCase()));
    } catch (e) {
      /* noop */
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    } else if (e.key === "Backspace" && !input && tags.length) {
      remove(tags.length - 1);
    }
  };

  const q = input.trim().replace(/^#+/, "").toLowerCase();
  const avail = suggestions
    .filter((s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase()))
    .filter((s) => (q ? s.toLowerCase().includes(q) : true));

  return (
    <div className="relative">
      <div
        className="flex flex-wrap items-center gap-2 bg-white border border-[#E3E8EE] px-2 py-2 focus-within:ring-1 focus-within:ring-[#465B7F]"
        data-testid={testid}
      >
        {tags.map((t, i) => (
          <span
            key={i}
            data-testid={`${testid}-chip-${i}`}
            className="doc-tag inline-flex items-center gap-1 text-xs font-mono-data px-2 py-1"
          >
            <Hash size={11} />
            {t}
            <button type="button" onClick={() => remove(i)} className="hover:text-[#ED1F26] ml-0.5" data-testid={`${testid}-remove-${i}`}>
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          value={input}
          data-testid={`${testid}-field`}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => setFocused(true)}
          onBlur={() => { setTimeout(() => setFocused(false), 150); add(input); }}
          placeholder={tags.length ? "" : "Agrega un tag y presiona Enter (ej. lonas)"}
          className="flex-1 min-w-[140px] bg-transparent font-mono-data text-sm py-0.5 focus:outline-none"
        />
      </div>
      {focused && avail.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E3E8EE] shadow-lg max-h-48 overflow-auto" data-testid={`${testid}-suggestions`}>
          {avail.map((s) => (
            <div
              key={s}
              className="flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-[#465B7F]/5 group"
            >
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); add(s); }}
                className="flex items-center gap-1.5 flex-1 text-left font-mono-data text-sm text-[#374A66]"
                data-testid={`${testid}-suggestion-${s}`}
              >
                <Hash size={11} /> {s}
              </button>
              <button
                type="button"
                title="Eliminar tag del catálogo (no se usa)"
                onMouseDown={(e) => { e.preventDefault(); deleteSuggestion(s); }}
                className="text-[#9aa3ac] hover:text-[#ED1F26] opacity-60 group-hover:opacity-100 transition-opacity"
                data-testid={`${testid}-delete-suggestion-${s}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TagsCell({ tags = [], testid }) {
  if (!tags || tags.length === 0) return <>—</>;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid={testid}>
      {tags.map((t, i) => (
        <span key={i} className="doc-tag inline-flex items-center gap-0.5 text-[11px] font-mono-data px-1.5 py-0.5">
          <Hash size={9} />{t}
        </span>
      ))}
    </span>
  );
}

export function TagsField({ tags, setTags, testid, label = "Tags (etiquetas del trabajo)" }) {
  return (
    <label className="block">
      <span className="overline block mb-1.5">{label}</span>
      <TagsInput tags={tags} setTags={setTags} testid={testid} />
    </label>
  );
}
